import { HttpResponse } from '../../interfaces/api/http';
import http from '../http';
import api from '../../config/api';
import { QuizDetails, SubmitQuizResponse } from '../../interfaces/api/quiz';

/**
 * Quiz service module.
 * Provides methods to interact with the quiz-related API endpoints.
 */
const quiz = {
  /**
   * Fetches details of a section quiz by its ID.
   *
   * @param {number} id - The ID of the section quiz.
   * @returns {Promise<HttpResponse<QuizDetails>>} - A promise resolving to the section quiz details.
   *
   * @example
   * quiz.getSectionQuizDetails(123).then((response) => {
   *   console.log(response.data);
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  getSectionQuizDetails: (id: number): Promise<HttpResponse<QuizDetails>> => {
    return http.get<QuizDetails>(`${api.getSectionQuiz}/${id}`);
  },

  /**
   * Fetches details of the final quiz by its ID.
   *
   * @param {number} id - The ID of the final quiz.
   * @returns {Promise<HttpResponse<QuizDetails>>} - A promise resolving to the final quiz details.
   *
   * @example
   * quiz.getFinalQuizDetails(456).then((response) => {
   *   console.log(response.data);
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  getFinalQuizDetails: (id: number): Promise<HttpResponse<QuizDetails>> => {
    return http.get<QuizDetails>(`${api.getFinalQuiz}/${id}`);
  },

  /**
   * Submits quiz answers for a specific quiz.
   *
   * @param {number} id - The ID of the quiz.
   * @param {any} body - The payload containing quiz answers.
   * @returns {Promise<HttpResponse<SubmitQuizResponse>>} - A promise resolving to the quiz submission response.
   *
   * @example
   * const answers = { question_id: 1, answer_ids: [2] };
   * quiz.submitQuiz(789, answers).then((response) => {
   *   console.log(response.data);
   * }).catch((error) => {
   *   console.error(error);
   * });
   */
  submitQuiz: (id: number, body: any): Promise<HttpResponse<SubmitQuizResponse>> => {
    return http.post<SubmitQuizResponse>(`${api.submitQuiz}/${id}`, body);
  },
};

export default quiz;
